import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AgentFile, AiAgent, AiAgentWithFacts } from './aiAgentsConsts';
import { Issue, ScanUpdate } from '../issues/issuesTypes';
import { ActiveScan, IssueStatus } from '@server/issues';
import { AgentFileStatus } from '@server/ai';

export interface AiAgentsState {
  environments: {
    [crmOrgId: string]: {
      agents?: AiAgentWithFacts[];
      isLoading: boolean;
    };
  };
}

const initialState: AiAgentsState = {
  environments: {},
};

export const aiAgentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    setAgentsLoading: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
      }>,
    ) => {
      const { crmOrgId } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = { isLoading: true };
        return;
      }
      state.environments[crmOrgId].isLoading = true; //don't override the existing agents data, to keep the UI live
    },
    setAgents: (
      state,
      action: PayloadAction<{
        crmOrgId?: string;
        agents: Omit<AiAgentWithFacts, 'crmOrgId'>[];
      }>,
    ) => {
      const { crmOrgId, agents } = action.payload;
      if (!crmOrgId) {
        return;
      }

      // Get existing agents to preserve issues data
      const existingAgents = state.environments[crmOrgId]?.agents || [];

      const agentsWithOrg = agents.map((agent) => {
        // Find existing agent with same ID to preserve issues data
        const existingAgent = existingAgents.find((existing) => existing.id === agent.id);

        // Preserve issue-related properties if they exist in the current state
        const preservedIssuesData = existingAgent
          ? {
              issues: existingAgent.issues,
              isLoadingIssues: existingAgent.isLoadingIssues,
              activeScan: existingAgent.activeScan,
            }
          : {};

        return {
          ...agent,
          crmOrgId,
          ...preservedIssuesData,
        };
      });

      state.environments[crmOrgId] = { agents: agentsWithOrg, isLoading: false };
    },
    addAgent: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agent: AiAgent;
      }>,
    ) => {
      const { crmOrgId, agent } = action.payload;
      if (!state.environments[crmOrgId]) {
        state.environments[crmOrgId] = { agents: [], isLoading: false };
      }
      if (!state.environments[crmOrgId].agents) {
        state.environments[crmOrgId].agents = [];
      }
      state.environments[crmOrgId].agents.push({ ...agent, facts: [] });
    },
    renameAgent: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        name: string;
      }>,
    ) => {
      const { crmOrgId, agentId, name } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      relevantAgent.name = name;
    },
    setAgentUserInstructions: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        userInstructions: string;
      }>,
    ) => {
      const { crmOrgId, agentId, userInstructions } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      relevantAgent.userInstructions = userInstructions;
    },
    setAgentSystemInstructions: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        systemInstructions: string;
      }>,
    ) => {
      const { crmOrgId, agentId, systemInstructions } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      relevantAgent.systemInstructions = systemInstructions;
    },
    addAgentFiles: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        files: AgentFile[];
      }>,
    ) => {
      const { crmOrgId, agentId, files } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      relevantAgent.files = [...(relevantAgent.files ?? []), ...files];
    },
    replaceAgentFile: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        tempId: string;
        file: AgentFile;
      }>,
    ) => {
      const { crmOrgId, agentId, tempId, file } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent || !relevantAgent.files) {
        return;
      }
      const fileIndex = relevantAgent.files.findIndex((f) => f.id === tempId);
      if (fileIndex !== -1) {
        relevantAgent.files[fileIndex] = file;
      }
    },
    updateAgentFileStatus: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        fileId: string;
        status: AgentFileStatus;
      }>,
    ) => {
      const { crmOrgId, agentId, fileId, status } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent || !relevantAgent.files) {
        return;
      }
      const file = relevantAgent.files.find((f) => f.id === fileId);
      if (file) {
        file.status = status;
      }
    },
    deleteAgentFile: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        fileId: string;
      }>,
    ) => {
      const { crmOrgId, agentId, fileId } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (relevantAgent) {
        relevantAgent.files = relevantAgent.files.filter((file) => file.id !== fileId);
      }
    },
    deleteAgent: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
      }>,
    ) => {
      const { crmOrgId, agentId } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      state.environments[crmOrgId].agents = state.environments[crmOrgId].agents.filter(
        (agent) => agent.id !== agentId,
      );
    },
    setIsLoadingIssues: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
      }>,
    ) => {
      const { crmOrgId, agentId } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      relevantAgent.isLoadingIssues = true;
    },
    setAgentIssues: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        issues: Issue[];
        activeScan?: ActiveScan;
      }>,
    ) => {
      const { crmOrgId, agentId, issues, activeScan } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      // Sort issues so that those with isNew: true appear first
      const sortedIssues = [...issues].sort((a, b) => {
        if (a.isNew && !b.isNew) return -1;
        if (!a.isNew && b.isNew) return 1;
        return 0;
      });
      relevantAgent.issues = sortedIssues;
      relevantAgent.isLoadingIssues = false;
      relevantAgent.activeScan = activeScan;
    },
    updateAgentIssueStatus: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        issueId: string;
        newStatus: IssueStatus;
      }>,
    ) => {
      const { crmOrgId, agentId, issueId, newStatus } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent || !relevantAgent.issues) {
        return;
      }
      const relevantIssue = relevantAgent.issues.find((issue) => issue.id === issueId);
      if (relevantIssue) {
        relevantIssue.status = newStatus;
      }
    },
    updateAgentScanStatus: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        scanUpdate: ScanUpdate;
      }>,
    ) => {
      const { crmOrgId, scanUpdate } = action.payload;
      const { agentId, status, newIssue, checkStatusCounts } = scanUpdate;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      relevantAgent.activeScan = {
        checkStatusCounts,
        status,
      };
      if (newIssue) {
        if (!relevantAgent.issues) {
          relevantAgent.issues = [];
        }
        //new issues should appear at the top of the table
        relevantAgent.issues.unshift(newIssue);
      }
    },
    addAgentBestPractices: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        bestPracticeIds: string[];
      }>,
    ) => {
      const { crmOrgId, agentId, bestPracticeIds } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent) {
        return;
      }
      // Initialize bestPractices array if it doesn't exist
      if (!relevantAgent.bestPractices) {
        relevantAgent.bestPractices = [];
      }
      // Add new best practices (avoid duplicates)
      bestPracticeIds.forEach((bestPracticeId) => {
        const exists = relevantAgent.bestPractices?.some(
          (bp) => bp.bestPracticeId === bestPracticeId,
        );
        if (!exists) {
          relevantAgent.bestPractices?.push({
            bestPracticeId,
            createdAt: new Date().toISOString(),
          });
        }
      });
    },
    removeAgentBestPractices: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        agentId: string;
        bestPracticeIds: string[];
      }>,
    ) => {
      const { crmOrgId, agentId, bestPracticeIds } = action.payload;
      if (!state.environments[crmOrgId]?.agents) {
        return;
      }
      const relevantAgent = state.environments[crmOrgId].agents.find(
        (agent) => agent.id === agentId,
      );
      if (!relevantAgent || !relevantAgent.bestPractices) {
        return;
      }
      // Remove specified best practices
      relevantAgent.bestPractices = relevantAgent.bestPractices.filter(
        (bp) => !bestPracticeIds.includes(bp.bestPracticeId),
      );
    },
  },
});

export const {
  setAgents,
  addAgent,
  renameAgent,
  deleteAgent,
  setAgentsLoading,
  setAgentUserInstructions,
  setAgentSystemInstructions,
  addAgentFiles,
  replaceAgentFile,
  updateAgentFileStatus,
  deleteAgentFile,
  setIsLoadingIssues,
  setAgentIssues,
  updateAgentIssueStatus,
  updateAgentScanStatus,
  addAgentBestPractices,
  removeAgentBestPractices,
} = aiAgentsSlice.actions;

export default aiAgentsSlice.reducer;
